import { api } from '@/api';
import {
  GetSuggestedTrackItMeasurementParameterFiltersRequestDtoTypeEnum,
  GetSuggestedTrackItMeasurementPropertyFiltersRequestDtoTypeEnum,
  instanceOfTrackItMeasurementPropertyFilterDto,
  ParameterCombinationDto,
  ParameterCombinationTableDto,
  TrackItMeasurementFilterForCombinationDto,
  TrackItMeasurementParameterFilterDto,
  TrackItMeasurementPropertyFilterDto,
} from '@/api/generator';
import { NotificationMessage } from '@/components/notifications/types';
import { AnyApiError, ApiError } from '@/errorHandling/typeGuards';
import { I18nKey } from '@/utils/typings';
import { toCreateParameterCombinationTableDto } from '@/views/tasks/mapping.ts';
import {
  applyTrackItFilterSelection,
  applyTrackItPropertyFilterSelection,
  deriveTrackItFilterCatalog,
  isParameterFilterEntry,
  selectedTrackItFilters,
} from './trackItFilters';
import { useTranslation } from 'i18next-vue';
import { NIL } from 'uuid';
import { computed, onMounted, Ref, ref } from 'vue';

interface UseTrackItFiltersDeps {
  unexpectedUpdateErrorNotification: Ref<NotificationMessage | undefined>;
  applyUpdate: () => Promise<void>;
  areaId: string;
}

export function useTrackItFilters(
  combinationTable: Ref<ParameterCombinationTableDto | null | undefined>,
  deps: UseTrackItFiltersDeps,
) {
  const { t } = useTranslation();

  // Undefined for an area not yet persisted.
  const taskItemAreaId = deps.areaId !== NIL ? deps.areaId : undefined;

  const trackItFilterCatalog = ref<TrackItMeasurementFilterForCombinationDto[]>([]);

  const filtersFromCombinations = computed(() =>
    selectedTrackItFilters(combinationTable.value?.combinations, false),
  );
  const propertiesFromCombinations = computed(() =>
    selectedTrackItFilters(combinationTable.value?.combinations, true),
  );

  function seedTrackItFilterCatalog(combinations: ParameterCombinationDto[]) {
    trackItFilterCatalog.value = deriveTrackItFilterCatalog(combinations);
  }

  // The table arrives with the area, so the catalog only needs seeding from what's already on it.
  onMounted(() => {
    if (combinationTable.value?.combinations?.length) {
      seedTrackItFilterCatalog(combinationTable.value.combinations);
    }
  });

  function currentlyPersistedFilters(): TrackItMeasurementFilterForCombinationDto[] {
    return deriveTrackItFilterCatalog(combinationTable.value?.combinations ?? []);
  }

  async function fetchSuggestedTrackItParameterFilters(
    table: ParameterCombinationTableDto | undefined,
    previousFilters: TrackItMeasurementParameterFilterDto[],
  ): Promise<TrackItMeasurementParameterFilterDto[]> {
    const { result } = await api.trackItImportApi.getSuggestedTrackItMeasurementParameterFilters({
      getSuggestedTrackItMeasurementParameterFiltersRequestDto: {
        combinationTable: table ? toCreateParameterCombinationTableDto(table) : undefined,
        taskItemAreaId,
        previousFilters,
        $type: Object.values(GetSuggestedTrackItMeasurementParameterFiltersRequestDtoTypeEnum)[0],
      },
    });
    return result ?? [];
  }

  async function fetchSuggestedTrackItPropertyFilters(): Promise<
    TrackItMeasurementPropertyFilterDto[]
  > {
    const { result } = await api.trackItImportApi.getSuggestedTrackItMeasurementPropertyFilters({
      getSuggestedTrackItMeasurementPropertyFiltersRequestDto: {
        previousFilters: currentlyPersistedFilters().filter(
          instanceOfTrackItMeasurementPropertyFilterDto,
        ),
        $type: Object.values(GetSuggestedTrackItMeasurementPropertyFiltersRequestDtoTypeEnum)[0],
      },
    });
    return result ?? [];
  }

  async function fetchSuggestedTrackItFiltersForModal() {
    const [parameterFilters, propertyFilters] = await Promise.all([
      fetchSuggestedTrackItParameterFilters(
        undefined,
        currentlyPersistedFilters().filter(isParameterFilterEntry),
      ),
      fetchSuggestedTrackItPropertyFilters(),
    ]);
    trackItFilterCatalog.value = [...parameterFilters, ...propertyFilters];
  }

  // A freshly selected property starts empty, which fails backend validation — surface as
  // guidance to fill it in, not a generic error.
  function emptyTrackItPropertyValueErrorHandler(error: AnyApiError) {
    const validationErrors =
      error.name === ApiError.badRequest ? error.error?.exception?.validationErrors : undefined;
    if (
      validationErrors?.length &&
      validationErrors.every((item) => item.memberNames?.includes('MeasurementPropertyName'))
    ) {
      deps.unexpectedUpdateErrorNotification.value = {
        type: 'warning',
        message: t('TrackItPropertyValueRequiredMessage' as I18nKey),
        closable: true,
      };
      return undefined;
    }
    return error;
  }

  async function requestSuggestedTrackItParameterFilters(
    stagedFilters: TrackItMeasurementParameterFilterDto[],
  ) {
    if (!combinationTable.value) return;
    const parameterFilters = await fetchSuggestedTrackItParameterFilters(
      combinationTable.value,
      stagedFilters,
    );
    trackItFilterCatalog.value = [
      ...trackItFilterCatalog.value.filter(instanceOfTrackItMeasurementPropertyFilterDto),
      ...parameterFilters,
    ];
  }

  async function applyTrackItFilters(
    selectedFilters: TrackItMeasurementParameterFilterDto[],
    selectedProperties: TrackItMeasurementPropertyFilterDto[],
  ) {
    if (!combinationTable.value?.combinations) return;

    combinationTable.value.combinations = applyTrackItPropertyFilterSelection(
      applyTrackItFilterSelection(combinationTable.value.combinations, selectedFilters),
      selectedProperties,
    );

    await deps.applyUpdate();

    if (combinationTable.value?.combinations) {
      seedTrackItFilterCatalog(combinationTable.value.combinations);
    }
  }

  return {
    trackItFilterCatalog,
    filtersFromCombinations,
    propertiesFromCombinations,
    fetchSuggestedTrackItFiltersForModal,
    emptyTrackItPropertyValueErrorHandler,
    requestSuggestedTrackItParameterFilters,
    applyTrackItFilters,
  };
}
